import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ChevronUp } from "lucide-react";
import StickyNav from "./components/StickyNav";
import GameSection from "./components/GameSection";
import GamePage from "./components/GamePage";
import LeaderboardPage from "./components/LeaderboardPage";
import HowToPlayPage from "./components/HowToPlayPage";

// Components
import HeroSection from "./components/HeroSection";
import UniverseIntro from "./components/UniverseIntro";
import TimelineSection from "./components/TimelineSection";
import BooksSection from "./components/BooksSection";
import CharactersSection from "./components/CharactersSection";
import SeriesSection from "./components/SeriesSection";
import CrewQuiz from "./components/CrewQuiz";
import AskTheWater from "./components/AskTheWater";
import EmailCapture from "./components/EmailCapture";
import Footer from "./components/Footer";

const LandingPage = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="relative min-h-screen bg-[#080C10]" data-testid="landing-page">
      {/* Film Grain Overlay */}
      <div className="film-grain" aria-hidden="true" />
      
      {/* Sticky Navigation */}
      <StickyNav />
      
      {/* Main Content */}
      <main>
        <HeroSection />
        <UniverseIntro />
        <TimelineSection />
        <BooksSection />
        <CharactersSection />
        <SeriesSection />
        <GameSection />
        <CrewQuiz />
        <EmailCapture />
      </main>
      
      <Footer />
      
      {/* Floating AI Chat */}
      <AskTheWater />

      {/* Scroll to Top */}
      <button
        data-testid="scroll-to-top-btn"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className={`fixed bottom-6 left-6 z-50 w-10 h-10 rounded-full bg-[#C9A84C]/20 border border-[#C9A84C]/40 flex items-center justify-center backdrop-blur-sm transition-all duration-300 hover:bg-[#C9A84C]/40 ${showScrollTop ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"}`}
        aria-label="Scroll to top"
      >
        <ChevronUp size={18} className="text-[#C9A84C]" />
      </button>
    </div>
  );
};

// Dynamic favicon + title per route
const RouteMeta = () => {
  const location = useLocation();
  useEffect(() => {
    const isGame = location.pathname.startsWith('/game');
    const link = document.querySelector("link[rel='icon']") || document.createElement('link');
    link.rel = 'icon';
    const apple = document.querySelector("link[rel='apple-touch-icon']");

    if (isGame) {
      document.title = 'KOW — THE GAME';
      link.href = '/kow-game-logo.png';
      if (apple) apple.href = '/kow-game-logo.png';
    } else {
      document.title = 'KILLING OVER WATER';
      link.href = '/favicon.ico';
      if (apple) apple.href = '/apple-touch-icon.png';
    }
  }, [location.pathname]);
  return null;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <RouteMeta />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/game" element={<GamePage />} />
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/how-to-play" element={<HowToPlayPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
